import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

// Custom hooks and components
import { useFormValidation, FormData } from '../hooks/useFormValidation';
import { FormField } from '../components/formComponents/FormField';
import { ErrorMessage } from '../components/formComponents/ErrorMessage';
import { DownloadConfirmationPopup } from '../components/downloadConfigPopup/DownloadConfirmationPopup';
import Toast from '../components/common/Toast';
import { downloadJSON } from '../utils/downloadUtils';
import Loading from '../components/common/Loading';
import ProcessSetting from '../api/processSetting';

// MUI DatePicker
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

// Digital HIG Components
// @ts-ignore
import DHTypography from "@digital-hig/mui/Typography";
// @ts-ignore
import DHBox from "@digital-hig/mui/Box";
// @ts-ignore
import DHBreadcrumbs from "@digital-hig/mui/Breadcrumbs";
// @ts-ignore
import DHLink from "@digital-hig/mui/Link";
// @ts-ignore
import DHButton from "@digital-hig/mui/Button";
// @ts-ignore
import DHPaper from "@digital-hig/mui/Paper";
// @ts-ignore
import DHTooltip from "@digital-hig/mui/Tooltip";
// @ts-ignore
import DHRadioGroup from "@digital-hig/mui/RadioGroup";
// @ts-ignore
import DHRadio from "@digital-hig/mui/Radio";
// @ts-ignore
import DHFormControlLabel from "@digital-hig/mui/FormControlLabel";
// @ts-ignore
import DHCheckbox from "@digital-hig/mui/Checkbox";
// @ts-ignore
import DHSelect from "@digital-hig/mui/Select";
// @ts-ignore
import DHMenuItem from "@digital-hig/mui/MenuItem";
// @ts-ignore
import { InfoOutline } from "@digital-hig/icon";
// @ts-ignore
import DHCircularProgress from "@digital-hig/mui/CircularProgress";

// Authentication
import { useCfpAuthUtilities } from '../providers/CfpAuthContext';

// Constants
const FORM_STYLES = {
  fontFamily: 'Artifakt Element, sans-serif !important',
  cardPadding: '32px',
  cardMargin: '24px',
  fieldMargin: '24px'
};

export default function GenerateConfigPage() {
  const navigate = useNavigate();
  const { cfpAuthUtils, isAuthenticated, loading } = useCfpAuthUtilities();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('Configuration generation failed. Please try again or contact support.');
  // Loading state for generate button
  const [isGenerating, setIsGenerating] = useState(false);
  // Settings error state
  const [settingsError, setSettingsError] = useState('');
  const [settingsTouched, setSettingsTouched] = useState(false);
  // Store last successful API response for re-download
  // Store last download info for re-download functionality
  const [lastDownloadInfo, setLastDownloadInfo] = useState<{ downloadUrl: string; cleanup: () => void } | null>(null);

  const [formData, setFormData] = useState<FormData>({
    customerName: '',
    accountCSN: '',
    serverDeviceID: '',
    contractEndDate: '',
    isUSDoD: '',
    impactLevel: '',
    version: '1.0.0',
    build: '',
    settingsValue: {
      disableProductUsage: false,
      disconnected: false,
      disableServiceSystem: false,
      readOnly: false,
      disableGenuineCompliance: false,
      disableLicensingCompliance: false,
      fedRamp: false
    }
  });

  const {
    fieldErrors,
    touchedFields,
    validateField,
    handleFieldBlur,
    clearFieldError,
    markAllFieldsTouched,
    setFieldErrors,
    setTouchedFields
  } = useFormValidation();

  // Cleanup stored data when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      // Cleanup on component unmount
      if (lastDownloadInfo) {
        lastDownloadInfo.cleanup();
        setLastDownloadInfo(null);
      }
    };
  }, [lastDownloadInfo]);

  // Check authentication on mount and redirect if not authenticated
  useEffect(() => {
    const checkAuth = async () => {
      logger.debug('🔍 GenerateConfigPage: Checking authentication status...');
      
      // Wait a bit for the auth context to initialize
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check if user is authenticated or has cached user info
      const cachedUserInfo = cfpAuthUtils.getCachedUserInfo?.();
      
      if (!isAuthenticated && !cachedUserInfo) {
        logger.warn('⚠️ GenerateConfigPage: User not authenticated, redirecting to sign in');
        navigate('/');
      } else {
        logger.info('✅ GenerateConfigPage: User is authenticated or has cached info');
      }
    };

    // Only check after loading is complete
    if (!loading) {
      checkAuth();
    }
  }, [isAuthenticated, loading, navigate, cfpAuthUtils]);

  // Helper function for error state
  const getErrorState = (fieldName: keyof typeof fieldErrors) => {
    const isTouched = touchedFields[fieldName];
    const hasError = !!fieldErrors[fieldName];
    return {
      error: isTouched && hasError,
      showError: isTouched && hasError,
      errorMessage: fieldErrors[fieldName]
    };
  };

  // Form handlers
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Validate customerName in real-time if field is touched
      if (field === 'customerName' && touchedFields.customerName) {
        const error = validateField('customerName', value, newData);
        setFieldErrors(prevErrors => ({ ...prevErrors, customerName: error }));
      }
      
      // Validate accountCSN in real-time if field is touched
      if (field === 'accountCSN' && touchedFields.accountCSN) {
        const error = validateField('accountCSN', value, newData);
        setFieldErrors(prevErrors => ({ ...prevErrors, accountCSN: error }));
      }
      
      // Validate serverDeviceID in real-time if field is touched
      if (field === 'serverDeviceID' && touchedFields.serverDeviceID) {
        const error = validateField(field, value, newData);
        setFieldErrors(prevErrors => ({ ...prevErrors, [field]: error }));
      }
      
      // Validate contractEndDate in real-time if field is touched
      if (field === 'contractEndDate' && touchedFields.contractEndDate) {
        const error = validateField('contractEndDate', value, newData);
        setFieldErrors(prevErrors => ({ ...prevErrors, contractEndDate: error }));
      }
      
      return newData;
    });
    
    // Clear error for fields that are not yet touched (avoid hiding errors on touched fields)
    if (field !== 'customerName' && field !== 'serverDeviceID' && field !== 'contractEndDate') {
      const key = (field === 'customerName' ? 'customerName' : field) as keyof typeof touchedFields;
      if (!touchedFields[key]) {
        clearFieldError(field);
      }
    }
  };

  const handleUSDoDChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      isUSDoD: value,
      impactLevel: value === 'no' ? '' : prev.impactLevel
    }));

    if (value === 'no') {
      clearFieldError('impactLevel');
      // Check if settings need validation
      const hasAtLeastOne = Object.values(formData.settingsValue).some(val => val === true);
      if (!hasAtLeastOne && settingsTouched) {
        setSettingsError('Atleast one setting must be selected');
      }
    } else {
      // Clear settings error when switching to "Yes"
      setSettingsError('');
      setSettingsTouched(false);
    }
  };

  const handleImpactLevelChange = (value: string) => {
    setFormData(prev => ({ ...prev, impactLevel: value }));
    clearFieldError('impactLevel');
  };

  const handleSettingChange = (setting: keyof typeof formData.settingsValue, checked: boolean) => {
    // Mark settings as touched when user interacts
    setSettingsTouched(true);
    
    setFormData(prev => {
      const newSettings = { ...prev.settingsValue };
      
      if (setting === 'disableGenuineCompliance' && checked) {
        newSettings.disableGenuineCompliance = true;
      } else if (setting === 'disconnected' && checked) {
        newSettings.disconnected = true;
        newSettings.disableProductUsage = true;
        newSettings.readOnly = false;
      } else if (setting === 'readOnly' && checked) {
        if (newSettings.disconnected) {
          return prev;
        }
        newSettings.readOnly = true;
      } else if (setting === 'disableProductUsage' && !checked) {
        if (newSettings.disconnected) {
          return prev;
        }
        newSettings.disableProductUsage = false;
      } else if (setting === 'disconnected' && !checked) {
        newSettings.disconnected = false;
        newSettings.disableProductUsage = false;
      } else if (setting === 'disableGenuineCompliance' && !checked) {
        newSettings.disableGenuineCompliance = false;
      } else if (setting === 'disableLicensingCompliance' && checked) {
        newSettings.disableLicensingCompliance = true;
      } else if (setting === 'disableLicensingCompliance' && !checked) {
        newSettings.disableLicensingCompliance = false;
      } else {
        newSettings[setting] = checked;
      }
      
      // Check if at least one setting is selected
      const hasAtLeastOne = Object.values(newSettings).some(val => val === true);
      if (hasAtLeastOne) {
        setSettingsError('');
      } else {
        setSettingsError('Atleast one setting must be selected');
      }
      
      return { ...prev, settingsValue: newSettings };
    });
  };

  // Form validation
  const isFormValid = () => {
    const basicFieldsValid = formData.customerName.trim() !== '' &&
                           formData.accountCSN.trim() !== '' &&
                           formData.serverDeviceID.trim() !== '' &&
                           formData.contractEndDate.trim() !== '' &&
                           formData.isUSDoD.trim() !== '';

    const usdodValid = formData.isUSDoD !== 'yes' || formData.impactLevel.trim() !== '';
    const settingsValid = formData.isUSDoD !== 'no' || 
                         Object.values(formData.settingsValue).some(setting => setting === true);

    // Check if contract end date is in the future
    const contractDateValid = () => {
      if (!formData.contractEndDate.trim()) return false;
      const contractDate = new Date(formData.contractEndDate);
      contractDate.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      return contractDate > today;
    };

    // Run field-level validation using the shared hook to catch format errors
    const fieldsToValidate: Array<keyof typeof fieldErrors> = [
      'customerName',
      'accountCSN',
      'serverDeviceID',
      'contractEndDate',
      'isUSDoD'
    ];
    if (formData.isUSDoD === 'yes') {
      fieldsToValidate.push('impactLevel');
    }
    const hasAnyFieldError = fieldsToValidate.some((field) => {
      const value = (formData as any)[field] as string;
      const error = validateField(field, value, formData);
      return !!error;
    });

    return basicFieldsValid && usdodValid && settingsValid && contractDateValid() && !hasAnyFieldError;
  };

  const handleGenerateFile = async () => {
    // Revalidate authentication to catch cross-tab logout before generating config
    const stillValid =  cfpAuthUtils.revalidateAuthentication();
    if (!stillValid) {
      logger.warn('⚠️ GenerateConfigPage: User not authenticated after revalidation, redirecting to sign in');
      navigate('/');
      return;
    }
    if (!isFormValid()) {
      markAllFieldsTouched();
      return;
    }
    
    // Clear previous download info when starting new generation
    if (lastDownloadInfo) {
      lastDownloadInfo.cleanup();
      setLastDownloadInfo(null);
    }
    setIsGenerating(true); // Disable button while processing

    try {
      // Get ID token from cfpAuthUtils
      let token: string;
      try {
        const tokenValue = cfpAuthUtils.getIdToken();
        if (!tokenValue) {
          throw new Error('No authentication token available');
        }
        token = tokenValue;
      } catch (authError) {
        logger.error('Authentication error:', authError);
        setToastMessage('Authentication failed. Please log in again.');
        setToastOpen(true);
        setIsGenerating(false);
        return;
      }
      
      // Create API instance and make request
      const processSetting = new ProcessSetting();
      const result = await processSetting.generateConfiguration(token, formData);
      
      if (result.success) {
        
        // Automatically download the JSON configuration file
        try {
          const filename = 'AdskDeviceSettingConfig';
          const downloadInfo = await downloadJSON(result.data, filename);
          setLastDownloadInfo(downloadInfo);
        } catch (downloadError) {
          logger.error('Automatic download failed:', downloadError);
          // Don't show error toast for automatic download failure
          // User can still download via "Download Again" button
        }
        
        setIsPopupOpen(true);
      } else {
        const errorMessage = result.error?.message || 'Please try again or contact support.';
        setToastMessage('Configuration generation failed. ' + errorMessage);
        setToastOpen(true);
        logger.error('Configuration generation failed:', result.error);
      }
    } catch (error) {
      logger.error('Error calling process-setting API:', error);
      setToastMessage('Configuration generation failed. Please check your connection and try again.');
      setToastOpen(true);
    } finally {
      setIsGenerating(false); // Re-enable button after processing
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    // Cleanup download info when popup is closed
    if (lastDownloadInfo) {
      lastDownloadInfo.cleanup();
      setLastDownloadInfo(null);
    }
  };

  const handleDownloadAgain = () => {
    // This function is now handled by the direct link in the popup
    // But we keep it as a fallback for cases where downloadUrl is not available
    if (!lastDownloadInfo) {
      logger.warn('No previous download info available for re-download');
      setToastMessage('No previous configuration available for download. Please generate a new configuration first.');
      setToastOpen(true);
      return;
    }

    // Fallback: trigger download using the stored URL
    window.open(lastDownloadInfo.downloadUrl, '_blank');
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  // Show loading while authentication is being checked
  if (loading) {
    return (
      <DHBox sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Loading message="Checking authentication..." />
      </DHBox>
    );
  }

  if (!isAuthenticated && !cfpAuthUtils.getCachedUserInfo?.()) {
    navigate('/');
  }

  return (
    <DHBox>
      {/* Main Content Area */}
      <DHBox 
        className="dhig-padding-4 dhig-max-width-1200 dhig-margin-auto"
        style={{ 
          padding: '40px 20px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}
      >
        {/* Breadcrumb Navigation */}
        <DHBreadcrumbs 
          aria-label="breadcrumb"
          style={{
            marginBottom: '16px'
          }}
        >
          <DHLink
            component="button"
            onClick={() => navigate('/home')}
            underline="none"
            sx={{
              fontFamily: 'Artifakt Element, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0%',
              textAlign: 'center'
            }}
          >
            Home
          </DHLink>
          <DHTypography 
            variant="body-copy-small"
            sx={{
              fontFamily: 'Artifakt Element, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0%',
              textAlign: 'center'
            }}
          >
            Generate configuration file
          </DHTypography>
        </DHBreadcrumbs>

        <DHTypography 
          variant="headline-large"
          className="dhig-typography-headline-large dhig-color-text-primary"
          style={{ 
            marginBottom: '32px',
            fontFamily: 'Artifakt Legend, sans-serif',
            fontWeight: 800,
            fontSize: '24px',
            lineHeight: '120%',
            letterSpacing: '0%'
          }}
        >
          Generate Configuration File
        </DHTypography>

        {/* Main Form Card */}
        <DHBox 
          className="dhig-background-white dhig-border-radius-2 dhig-padding-4 dhig-box-shadow-1 dhig-max-width-1600"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: FORM_STYLES.cardPadding,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            maxWidth: '1600px',
            fontFamily: FORM_STYLES.fontFamily
          }}
        >
          {/* Customer Information Section */}
          <DHTypography 
            variant="headline-medium"
            className="dhig-typography-headline-medium dhig-color-text-primary"
            style={{
              marginBottom: FORM_STYLES.fieldMargin,
              marginTop: '0px',
              fontFamily: 'Artifakt Element, sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '150%',
              letterSpacing: '0%',
              verticalAlign: 'middle'
            }}
          >
            Enter customer information
          </DHTypography>
          
          {/* Two-column layout for form fields - responsive */}
          <DHBox 
            className="dhig-display-grid dhig-grid-template-columns-1fr-1fr dhig-gap-6"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '1fr 1fr' },
              gap: FORM_STYLES.fieldMargin
            }}
          >
            {/* Left Column */}
            <DHBox>
              {/* Account name */}
              <DHBox className="dhig-margin-bottom-6" style={{ marginBottom: FORM_STYLES.fieldMargin }}>
                <FormField
                  label="Account name"
                  value={formData.customerName}
                  onChange={(e) => handleInputChange('customerName', e.target.value)}
                  onBlur={() => handleFieldBlur('customerName', formData)}
                  error={getErrorState('customerName').error}
                  errorMessage={getErrorState('customerName').errorMessage}
                  showError={getErrorState('customerName').showError}
                  disabled={isGenerating}
                />
              </DHBox>

              {/* Server Device ID */}
              <DHBox className="dhig-margin-bottom-6" style={{ marginBottom: FORM_STYLES.fieldMargin }}>
                <DHBox 
                  className="dhig-display-flex dhig-align-center dhig-margin-bottom-2"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}
                >
                  <DHTypography 
                    variant="body-copy-medium"
                    className="dhig-typography-body-copy-medium dhig-color-text-primary"
                    style={{
                      fontFamily: 'Artifakt Element, sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16px',
                      letterSpacing: '0%',
                      verticalAlign: 'bottom'
                    }}
                  >
                    Server Device ID (generated)
                  </DHTypography>
                  <DHTooltip title="This is a generated device ID for server identification">
                  <DHBox
                    component="span"
                    style={{
                      display: 'inline-flex',
                      width: '20px',
                      height: '20px',
                      minWidth: '20px',
                      minHeight: '20px',
                      marginLeft: '8px',
                      cursor: 'pointer',
                      color: '#666666',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <InfoOutline style={{ width: '20px', height: '20px', display: 'block' }} />
                  </DHBox>
                  </DHTooltip>
                </DHBox>
                <FormField
                  label=""
                  value={formData.serverDeviceID}
                  onChange={(e) => handleInputChange('serverDeviceID', e.target.value)}
                  onBlur={() => handleFieldBlur('serverDeviceID', formData)}
                  error={getErrorState('serverDeviceID').error}
                  errorMessage={getErrorState('serverDeviceID').errorMessage}
                  showError={getErrorState('serverDeviceID').showError}
                  disabled={isGenerating}
                />
              </DHBox>

              {/* Settings Tool version display */}
              <DHBox className="dhig-margin-bottom-6" style={{ marginBottom: FORM_STYLES.fieldMargin }}>
                <DHTypography 
                  variant="body-copy-medium"
                  className="dhig-typography-body-copy-medium dhig-color-text-primary"
                  style={{
                    display: 'block',
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '16px',
                    letterSpacing: '0%',
                    verticalAlign: 'bottom',
                    marginBottom: '8px'
                  }}
                >
                  Settings Tool version
                </DHTypography>
                <DHPaper 
                  elevation={0}
                  style={{
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#333333',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '0px',
                    boxShadow: 'none'
                  }}
                >
                  <DHTypography 
                    variant="body-copy-medium"
                    className="dhig-typography-body-copy-medium dhig-color-text-secondary"
                    style={{
                      fontFamily: 'Artifakt Element, sans-serif',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      verticalAlign: 'middle',
                      color: '#333333'
                    }}
                  >
                    1.0.0
                  </DHTypography>
                </DHPaper>
              </DHBox>
            </DHBox>

            {/* Right Column */}
            <DHBox>
              {/* Account CSN */}
              <DHBox className="dhig-margin-bottom-6" style={{ marginBottom: FORM_STYLES.fieldMargin }}>
                <FormField
                  label="Account CSN"
                  value={formData.accountCSN}
                  onChange={(e) => handleInputChange('accountCSN', e.target.value)}
                  onBlur={() => handleFieldBlur('accountCSN', formData)}
                  error={getErrorState('accountCSN').error}
                  errorMessage={getErrorState('accountCSN').errorMessage}
                  showError={getErrorState('accountCSN').showError}
                  disabled={isGenerating}
                />
              </DHBox>

              {/* Contract end date */}
              <DHBox className="dhig-margin-bottom-6" style={{ marginBottom: FORM_STYLES.fieldMargin }}>
                <DHBox 
                  className="dhig-display-flex dhig-align-center dhig-margin-bottom-2"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}
                >
                  <DHTypography 
                    variant="body-copy-medium"
                    className="dhig-typography-body-copy-medium dhig-color-text-primary"
                    style={{
                      fontFamily: 'Artifakt Element, sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16px',
                      letterSpacing: '0%',
                      verticalAlign: 'bottom'
                    }}
                  >
                    Contract end date
                  </DHTypography>
                  <DHTooltip title="Enter the contract expiration date">
                    <DHBox
                      component="span"
                      style={{
                        display: 'inline-flex',
                        width: '20px',
                        height: '20px',
                        minWidth: '20px',
                        minHeight: '20px',
                        marginLeft: '8px',
                        cursor: 'pointer',
                        color: '#666666',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <InfoOutline style={{ width: '20px', height: '20px', display: 'block' }} />
                    </DHBox>
                  </DHTooltip>
                </DHBox>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.contractEndDate ? dayjs(formData.contractEndDate) : null}
                    onChange={(newValue: Dayjs | null) => {
                      const dateString = newValue ? newValue.format('YYYY-MM-DD') : '';
                      // Mark field as touched when date is selected (deliberate user action)
                      if (!touchedFields.contractEndDate) {
                        setTouchedFields(prev => ({ ...prev, contractEndDate: true }));
                      }
                      // Update form data and validate in real-time
                      setFormData(prev => {
                        const newData = { ...prev, contractEndDate: dateString };
                        const error = validateField('contractEndDate', dateString, newData);
                        setFieldErrors(prevErrors => ({ ...prevErrors, contractEndDate: error }));
                        return newData;
                      });
                    }}
                    disabled={isGenerating}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        onBlur: () => handleFieldBlur('contractEndDate', formData),
                        error: getErrorState('contractEndDate').error,
                        placeholder: "dd/mm/yyyy",
                        sx: {
                          width: '100%',
                          maxWidth: '410px',
                          height: '40px',
                          opacity: 1,
                          '& .MuiInputBase-root': {
                            height: '40px',
                            paddingTop: '8px',
                            paddingRight: '12px',
                            paddingBottom: '8px',
                            paddingLeft: '8px'
                          },
                          '& .MuiInputBase-input': {
                            fontFamily: 'Artifakt Element, sans-serif !important',
                            fontWeight: '600 !important',
                            fontSize: '16px !important',
                            lineHeight: '20px !important',
                            letterSpacing: '0% !important',
                            verticalAlign: 'middle !important',
                            padding: '0 8px !important',
                            '&::placeholder': {
                              color: 'rgba(0, 0, 0, 0.6)',
                              opacity: 1
                            }
                          },
                          '&.Mui-disabled': {
                            backgroundColor: 'white !important'
                          },
                          '& .MuiInputBase-root.Mui-disabled': {
                            backgroundColor: 'white !important',
                            color: 'rgba(0, 0, 0, 0.38) !important'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: getErrorState('contractEndDate').error 
                              ? '#d32f2f' 
                              : 'rgba(0, 0, 0, 0.23)'
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
                <ErrorMessage 
                  show={getErrorState('contractEndDate').showError} 
                  message={getErrorState('contractEndDate').errorMessage} 
                />
              </DHBox>
            </DHBox>
          </DHBox>

          {/* US Department of Defence Question Section and Impact Level - Side by Side - responsive */}
          <DHBox 
            className="dhig-display-grid dhig-grid-template-columns-1fr-1fr dhig-gap-6"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '1fr 1fr' },
              gap: FORM_STYLES.fieldMargin,
              marginTop: FORM_STYLES.fieldMargin
            }}
          >
            {/* Left Column - US DoD Question */}
            <DHBox>
              <DHTypography 
                variant="headline-medium"
                className="dhig-typography-headline-medium dhig-color-text-primary"
                style={{
                  fontFamily: 'Artifakt Element, sans-serif',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0%',
                  verticalAlign: 'bottom',
                  marginBottom: '16px',
                  marginTop: '0px'
                }}
              >
                Are you creating Settings for US Department of Defence?
              </DHTypography>
              
              <DHRadioGroup
                value={formData.isUSDoD}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUSDoDChange(e.target.value)}
                onBlur={() => handleFieldBlur('isUSDoD', formData)}
                className="dhig-display-flex dhig-flex-direction-column dhig-gap-3"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
                sx={{
                  width: 'fit-content',
                  alignItems: 'flex-start'
                }}
              >
                <DHFormControlLabel 
                  value="yes" 
                  control={<DHRadio disabled={isGenerating} />} 
                  label="Yes"
                  disabled={isGenerating}
                  sx={{
                    display: 'inline-flex',
                    width: 'fit-content',
                    m: 0,
                    p: 0,
                    '& .MuiFormControlLabel-label': {
                      fontFamily: 'Artifakt Element, sans-serif',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      verticalAlign: 'middle'
                    }
                  }}
                />
                <DHFormControlLabel 
                  value="no" 
                  control={<DHRadio disabled={isGenerating} />} 
                  label="No"
                  disabled={isGenerating}
                  sx={{
                    display: 'inline-flex',
                    width: 'fit-content',
                    m: 0,
                    p: 0,
                    '& .MuiFormControlLabel-label': {
                      fontFamily: 'Artifakt Element, sans-serif',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      verticalAlign: 'middle'
                    }
                  }}
                />
              </DHRadioGroup>
              <ErrorMessage 
                show={getErrorState('isUSDoD').showError} 
                message={getErrorState('isUSDoD').errorMessage} 
              />
            </DHBox>

            {/* Right Column - Impact Level (only when Yes is selected) */}
            {formData.isUSDoD === 'yes' && (
              <DHBox>
                <DHBox 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '16px',
                    marginTop: '0px'
                  }}
                >
                  <DHTypography 
                    variant="headline-medium"
                    className="dhig-typography-headline-medium dhig-color-text-primary"
                    style={{
                      fontFamily: 'Artifakt Element, sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16px',
                      letterSpacing: '0%',
                      verticalAlign: 'bottom'
                    }}
                  >
                    Impact level
                  </DHTypography>
                  <DHTooltip title="Choose the appropriate impact level for your deployment">
                    <DHBox
                      component="span"
                      style={{
                        display: 'inline-flex',
                        width: '20px',
                        height: '20px',
                        minWidth: '20px',
                        minHeight: '20px',
                        marginLeft: '8px',
                        cursor: 'pointer',
                        color: '#666666',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <InfoOutline style={{ width: '20px', height: '20px', display: 'block' }} />
                    </DHBox>
                  </DHTooltip>
                </DHBox>
                
                <DHSelect
                  value={formData.impactLevel}
                  onChange={(e: React.ChangeEvent<{ value: string }>) => handleImpactLevelChange(e.target.value)}
                  onBlur={() => handleFieldBlur('impactLevel', formData)}
                  error={getErrorState('impactLevel').error}
                  disabled={isGenerating}
                  sx={{
                    width: '100%',
                    maxWidth: '410px',
                    height: '40px',
                    opacity: 1,
                    '& .MuiInputBase-root': {
                      height: '40px',
                      paddingTop: '8px',
                      paddingRight: '12px',
                      paddingBottom: '8px',
                      paddingLeft: '8px'
                    },
                    '& .MuiSelect-select': {
                      fontFamily: 'Artifakt Element, sans-serif !important',
                      fontWeight: '600 !important',
                      fontSize: '16px !important',
                      lineHeight: '20px !important',
                      letterSpacing: '0% !important',
                      verticalAlign: 'middle !important',
                      paddingLeft: '8px !important'
                    }
                  }}
                >
                  <DHMenuItem 
                    value="2"
                    sx={{
                      fontFamily: 'Artifakt Element, sans-serif !important',
                      fontWeight: '600 !important',
                      fontSize: '16px !important',
                      lineHeight: '20px !important',
                      letterSpacing: '0% !important'
                    }}
                  >
                    2
                  </DHMenuItem>
                  <DHMenuItem 
                    value="4,5,6"
                    sx={{
                      fontFamily: 'Artifakt Element, sans-serif !important',
                      fontWeight: '600 !important',
                      fontSize: '16px !important',
                      lineHeight: '20px !important',
                      letterSpacing: '0% !important'
                    }}
                  >
                    4, 5 or 6
                  </DHMenuItem>
                </DHSelect>
                <ErrorMessage 
                  show={getErrorState('impactLevel').showError} 
                  message={getErrorState('impactLevel').errorMessage} 
                />
              </DHBox>
            )}
          </DHBox>

        </DHBox>

        {/* Settings card - NEW SEPARATE CARD below main form when No is selected */}
        {formData.isUSDoD === 'no' && (
          <DHBox 
            className="dhig-background-white dhig-border-radius-2 dhig-padding-4 dhig-box-shadow-1 dhig-max-width-1600"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: FORM_STYLES.cardPadding,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              maxWidth: '1600px',
              fontFamily: FORM_STYLES.fontFamily,
              marginTop: FORM_STYLES.fieldMargin
            }}
          >
            <DHTypography 
              variant="headline-medium"
              className="dhig-typography-headline-medium dhig-color-text-primary"
              style={{
                fontFamily: 'Artifakt Element, sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: '150%',
                letterSpacing: '0%',
                verticalAlign: 'middle',
                marginBottom: '16px',
                marginTop: '0px'
              }}
            >
              Select Applicable Settings{' '}
              <span style={{
                fontFamily: 'Artifakt Element, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '125%',
                letterSpacing: '0%',
                verticalAlign: 'middle'
              }}>
                (min. 1)
              </span>
            </DHTypography>
            
            <DHBox 
              className="dhig-display-flex dhig-flex-direction-column dhig-gap-3"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}
            >
              <DHFormControlLabel
                control={
                  <DHCheckbox
                    checked={formData.settingsValue.disableProductUsage}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSettingChange('disableProductUsage', e.target.checked)}
                    disabled={isGenerating || formData.settingsValue.disconnected}
                  />
                }
                label="Disable product usage data collection"
                disabled={isGenerating || formData.settingsValue.disconnected}
                sx={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  m: 0,
                  p: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    verticalAlign: 'middle'
                  }
                }}
              />
              
              <DHFormControlLabel
                control={
                  <DHCheckbox
                    checked={formData.settingsValue.disconnected}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSettingChange('disconnected', e.target.checked)}
                    disabled={isGenerating}
                  />
                }
                label="Disconnected (disables all Internet connectivity)"
                disabled={isGenerating}
                sx={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  m: 0,
                  p: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    verticalAlign: 'middle'
                  }
                }}
              />
              
              <DHFormControlLabel
                control={
                  <DHCheckbox
                    checked={formData.settingsValue.disableServiceSystem}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSettingChange('disableServiceSystem', e.target.checked)}
                    disabled={isGenerating}
                  />
                }
                label="Disable Service running in System context"
                disabled={isGenerating}
                sx={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  m: 0,
                  p: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    verticalAlign: 'middle'
                  }
                }}
              />
              
              <DHFormControlLabel
                control={
                  <DHCheckbox
                    checked={formData.settingsValue.readOnly}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSettingChange('readOnly', e.target.checked)}
                    disabled={isGenerating || formData.settingsValue.disconnected}
                  />
                }
                label="Read Only (disables sending product data)"
                disabled={isGenerating || formData.settingsValue.disconnected}
                sx={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  m: 0,
                  p: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    verticalAlign: 'middle'
                  }
                }}
              />
              
              <DHFormControlLabel
                control={
                  <DHCheckbox
                    checked={formData.settingsValue.disableGenuineCompliance}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSettingChange('disableGenuineCompliance', e.target.checked)}
                    disabled={isGenerating}
                  />
                }
                label="Disable Genuine Compliance data collection"
                disabled={isGenerating}
                sx={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  m: 0,
                  p: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    verticalAlign: 'middle'
                  }
                }}
              />
              
              <DHFormControlLabel
                control={
                  <DHCheckbox
                    checked={formData.settingsValue.disableLicensingCompliance}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSettingChange('disableLicensingCompliance', e.target.checked)}
                    disabled={isGenerating}
                  />
                }
                label="Disable Licensing Compliance data collection"
                disabled={isGenerating}
                sx={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  m: 0,
                  p: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    verticalAlign: 'middle'
                  }
                }}
              />
              
              <DHFormControlLabel
                control={
                  <DHCheckbox
                    checked={formData.settingsValue.fedRamp}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSettingChange('fedRamp', e.target.checked)}
                    disabled={isGenerating}
                  />
                }
                label="FedRamp compliant"
                disabled={isGenerating}
                sx={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  m: 0,
                  p: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    verticalAlign: 'middle'
                  }
                }}
              />
            </DHBox>
            
            {/* Settings error message */}
            <ErrorMessage 
              show={settingsTouched && !!settingsError} 
              message={settingsError} 
            />
          </DHBox>
        )}

        {/* Generate file button */}
        <DHBox 
          className="dhig-margin-top-6"
          style={{
            marginTop: FORM_STYLES.fieldMargin
          }}
        >
          <DHButton
            variant="contained"
            color="primary"
            onClick={handleGenerateFile}
            disabled={!isFormValid() || isGenerating}
            className="dhig-button-contained dhig-button-primary"
            sx={{
              padding: '12px 24px',
              minWidth: '140px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Artifakt Element, sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '20px',
              letterSpacing: '0%',
              textAlign: 'center',
              '& .MuiButton-startIcon': {
                display: 'flex !important',
                alignItems: 'center',
                marginRight: '8px',
                '& .DhigCircularProgress--background .MuiCircularProgress-circle': {
                  stroke: 'rgba(255, 255, 255, 0.3) !important',
                },
                '& .DhigCircularProgress--foreground .MuiCircularProgress-circle': {
                  stroke: '#FFFFFF !important',
                },
                '& .MuiCircularProgress-root.DhigCircularProgress--background': {
                  color: 'rgba(255, 255, 255, 0.3) !important',
                },
                '& .MuiCircularProgress-root.DhigCircularProgress--foreground': {
                  color: '#FFFFFF !important',
                },
                '& svg circle.MuiCircularProgress-circleDeterminate': {
                  stroke: 'rgba(255, 255, 255, 0.3) !important',
                },
                '& svg circle.MuiCircularProgress-circleIndeterminate': {
                  stroke: '#FFFFFF !important',
                }
              },
              '&:hover': {
                padding: '12px 24px',
                minWidth: '140px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Artifakt Element, sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '20px',
                letterSpacing: '0%',
                textAlign: 'center',
                transform: 'none',
                textDecoration: 'none',
                boxShadow: 'none'
              }
            }}
            startIcon={
              isGenerating ? (
                <DHCircularProgress
                  role="progressbar"
                  size="x-small"
                />
              ) : null
            }
          >
            {isGenerating ? 'Generating...' : 'Generate file'}
          </DHButton>
        </DHBox>
      </DHBox>

      {/* Generate File Confirmation Popup */}
      <DownloadConfirmationPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onDownloadAgain={handleDownloadAgain}
        customerName={formData.customerName}
        accountCSN={formData.accountCSN}
        downloadUrl={lastDownloadInfo?.downloadUrl}
      />
      {/* Toast Notification */}
      <Toast
        open={toastOpen}
        onClose={handleToastClose}
        type="error"
        header="Unable to generate config file."
        message={toastMessage}
        duration={6000}
        position={{ top: '116px', right: '20px' }}
      />
    </DHBox>
  );
}
